/**
 * Export compositor. The live uPlot canvas only carries the plot area, axes,
 * branding, and the on-canvas discrete legend. The rest of the chart's context
 * - the series legend, the continuous color-by legend, the filter summary, the
 * changeover legend, and the fit readout - are DOM elements that never make it
 * into a `canvas.toDataURL()`. This module rebuilds those strips onto an
 * enlarged canvas (from their backing atoms/data, not by scraping the DOM) so
 * PNG/JPG/PDF exports are a faithful, self-contained snapshot of the chart.
 */

import { runtime } from '../plot-runtime.ts';
import { plotState } from '../../../state/plot-state.ts';
import { getTheme } from '../../../ui/theme.ts';
import { buildSeriesColorer, fitColorFor } from '../plot-constants.ts';
import { plotFocusShip } from '../plot-ui-state.ts';
import { colorScaleRgb } from './color-scale.ts';
import { getCssVar } from './chart-dom.ts';
import {
  colorLegend,
  plotFilterSummary,
  changeoverLegend,
  fitReadout,
} from '../plot-ui-state.ts';

/** Solid background painted under the export (uPlot draws transparent). */
const BG_LIGHT = '#ffffff';
const BG_DARK = '#1a1a2e';

const dpr = (): number => window.devicePixelRatio || 1;

interface Chip {
  text: string;
  /** Swatch color; omit for a plain text chip. */
  color?: string;
  /** Render the swatch as a dashed line (fit overlays). */
  dashed?: boolean;
}

interface Metrics {
  d: number;
  pad: number; // band horizontal inset
  vpad: number; // band vertical padding
  lineH: number;
  rowGap: number;
  chipGap: number;
  swSize: number;
  swGap: number;
  font: string;
  labelFont: string;
}

function metrics(): Metrics {
  const d = dpr();
  return {
    d,
    pad: Math.round(14 * d),
    vpad: Math.round(8 * d),
    lineH: Math.round(16 * d),
    rowGap: Math.round(4 * d),
    chipGap: Math.round(14 * d),
    swSize: Math.round(11 * d),
    swGap: Math.round(6 * d),
    font: `500 ${Math.round(12 * d)}px system-ui, -apple-system, sans-serif`,
    labelFont: `700 ${Math.round(11 * d)}px system-ui, -apple-system, sans-serif`,
  };
}

/* ── Data models gathered from atoms / runtime ──────────── */

function seriesChips(): Chip[] {
  const data = runtime.lastData;
  if (!data) return [];
  const s = plotState.value;
  const chips: Chip[] = [];
  // Map plots aren't exported via the uPlot image path, and a heatmap's or
  // correlation matrix's colour is its own legend strip - none has series chips.
  if (
    data.plot_type === 'map' ||
    data.plot_type === 'heatmap' ||
    data.plot_type === 'correlation' ||
    data.plot_type === 'pie' ||
    data.plot_type === 'box' ||
    data.plot_type === 'number' ||
    data.plot_type === 'gauge' ||
    data.plot_type === 'sankey' ||
    data.plot_type === 'table'
  ) {
    return chips;
  }
  // The same colourer the renderers use. A positional palette lookup would put
  // the wrong swatch next to every multi-ship series, since those are coloured
  // by ship/signal rather than by index - an exported legend that disagreed
  // with the lines beside it.
  const colorOf = buildSeriesColorer(s, data.series, plotFocusShip.value);
  if (data.plot_type === 'histogram') {
    data.series.forEach((ser, i) => chips.push({ text: ser.name, color: colorOf(ser, i) }));
    return chips;
  }
  data.series.forEach((ser, i) => chips.push({ text: ser.name, color: colorOf(ser, i) }));
  for (const f of data.fits ?? []) {
    if (f.error || !Array.isArray(f.y)) continue;
    const baseIdx = data.series.findIndex((ser) => ser.name === f.series);
    const base = baseIdx >= 0 ? data.series[baseIdx] : null;
    const color = base ? colorOf(base, baseIdx) : fitColorFor(s, f.series, 0);
    chips.push({ text: `${f.series} · fit`, color, dashed: true });
  }
  return chips;
}

function changeoverChips(): { chips: Chip[]; emptyText: string | null } | null {
  const cl = changeoverLegend.value;
  if (!cl) return null;
  if (cl.empty) return { chips: [], emptyText: 'No changeovers in this range.' };
  const chips: Chip[] = cl.groups.map((g) => ({ text: g.display, color: g.solid }));
  if (cl.engines.length) chips.push({ text: `Engines: ${cl.engines.join(', ')}` });
  return { chips, emptyText: null };
}

/* ── Low-level drawing primitives ───────────────────────── */

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawSwatch(
  ctx: CanvasRenderingContext2D,
  x: number,
  cy: number,
  size: number,
  color: string,
  dashed: boolean,
  border: string,
): void {
  if (dashed) {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, Math.round(2.25 * dpr()));
    ctx.setLineDash([Math.round(5 * dpr()), Math.round(3 * dpr())]);
    ctx.beginPath();
    ctx.moveTo(x, cy);
    ctx.lineTo(x + size, cy);
    ctx.stroke();
    ctx.restore();
    return;
  }
  ctx.fillStyle = color;
  roundRectPath(ctx, x, cy - size / 2, size, size, Math.round(2 * dpr()));
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = Math.max(1, Math.round(dpr()));
  roundRectPath(ctx, x, cy - size / 2, size, size, Math.round(2 * dpr()));
  ctx.stroke();
}

/**
 * Flow `chips` left→right within `[x, x+maxW]`, wrapping to new rows. Returns
 * the height consumed. With `draw=false` it only measures (no fills), so the
 * same call sizes the band and then paints it at the right offset.
 */
function flowChips(
  ctx: CanvasRenderingContext2D,
  chips: Chip[],
  x: number,
  y: number,
  maxW: number,
  m: Metrics,
  textColor: string,
  border: string,
  draw: boolean,
): number {
  ctx.font = m.font;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  let cx = x;
  let cy = y;
  let rows = chips.length ? 1 : 0;
  for (const chip of chips) {
    const hasSw = chip.color !== undefined;
    const tw = ctx.measureText(chip.text).width;
    const cw = (hasSw ? m.swSize + m.swGap : 0) + tw;
    if (cx > x && cx + cw > x + maxW) {
      cx = x;
      cy += m.lineH + m.rowGap;
      rows++;
    }
    if (draw) {
      let tx = cx;
      if (hasSw) {
        drawSwatch(ctx, cx, cy + m.lineH / 2, m.swSize, chip.color!, !!chip.dashed, border);
        tx = cx + m.swSize + m.swGap;
      }
      ctx.fillStyle = textColor;
      ctx.fillText(chip.text, tx, cy + m.lineH / 2);
    }
    cx += cw + m.chipGap;
  }
  return rows * m.lineH + Math.max(0, rows - 1) * m.rowGap;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (const word of words) {
    const next = cur ? `${cur} ${word}` : word;
    if (!cur || ctx.measureText(next).width <= maxW) cur = next;
    else {
      lines.push(cur);
      cur = word;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

/* ── Bands ──────────────────────────────────────────────── */

type BandFn = (ctx: CanvasRenderingContext2D, w: number, y: number, m: Metrics, draw: boolean) => number;

/** A label + flowing chips band (series legend, changeover legend). */
function chipBand(label: string, chips: Chip[], emptyText: string | null): BandFn | null {
  if (!chips.length && !emptyText) return null;
  return (ctx, w, y, m, draw) => {
    const textColor = getCssVar('--text') || '#e8ebf4';
    const dimColor = getCssVar('--text-dim') || '#8a9099';
    const border = getCssVar('--border') || '#2a2d36';
    ctx.font = m.labelFont;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    const labelW = ctx.measureText(label).width + m.swGap * 2;
    const contentX = m.pad + labelW;
    const contentMaxW = w - contentX - m.pad;
    if (draw) {
      ctx.fillStyle = dimColor;
      ctx.fillText(label, m.pad, y + m.vpad + m.lineH / 2);
    }
    let h: number;
    if (emptyText) {
      if (draw) {
        ctx.font = m.font;
        ctx.fillStyle = dimColor;
        ctx.fillText(emptyText, contentX, y + m.vpad + m.lineH / 2);
      }
      h = m.lineH;
    } else {
      h = flowChips(ctx, chips, contentX, y + m.vpad, contentMaxW, m, textColor, border, draw);
    }
    return h + m.vpad * 2;
  };
}

/** Filter summary band: a bold label + wrapped plain text. */
function filterBand(summary: string): BandFn {
  return (ctx, w, y, m, draw) => {
    const dimColor = getCssVar('--text-dim') || '#8a9099';
    const textColor = getCssVar('--text') || '#e8ebf4';
    ctx.font = m.labelFont;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const label = 'Filters';
    const labelW = ctx.measureText(label).width + m.swGap * 2;
    const contentX = m.pad + labelW;
    const contentMaxW = w - contentX - m.pad;
    ctx.font = m.font;
    const lines = wrapLines(ctx, summary, contentMaxW);
    if (draw) {
      ctx.font = m.labelFont;
      ctx.fillStyle = dimColor;
      ctx.fillText(label, m.pad, y + m.vpad + m.lineH / 2);
      ctx.font = m.font;
      ctx.fillStyle = textColor;
      lines.forEach((ln, i) =>
        ctx.fillText(ln, contentX, y + m.vpad + m.lineH / 2 + i * (m.lineH + m.rowGap)),
      );
    }
    const h = lines.length * m.lineH + Math.max(0, lines.length - 1) * m.rowGap;
    return h + m.vpad * 2;
  };
}

/** Continuous color-by legend: label + min · gradient bar · max. */
function colorBarBand(): BandFn | null {
  const cl = colorLegend.value;
  if (!cl || cl.min == null || cl.max == null) return null;
  const scaleName = plotState.value.color_scale;
  return (ctx, w, y, m, draw) => {
    const textColor = getCssVar('--text') || '#e8ebf4';
    const dimColor = getCssVar('--text-dim') || '#8a9099';
    const border = getCssVar('--border') || '#2a2d36';
    const cyMid = y + m.vpad + m.lineH / 2;
    const barW = Math.round(160 * m.d);
    const barH = Math.round(10 * m.d);
    if (draw) {
      ctx.font = m.labelFont;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = dimColor;
      ctx.fillText(`${cl.colorBy}`, m.pad, cyMid);
      const labelW = ctx.measureText(`${cl.colorBy}`).width;
      let x = m.pad + labelW + m.chipGap;
      ctx.font = m.font;
      ctx.fillStyle = textColor;
      const minStr = String(cl.min);
      ctx.fillText(minStr, x, cyMid);
      x += ctx.measureText(minStr).width + m.swGap;
      const grad = ctx.createLinearGradient(x, 0, x + barW, 0);
      for (let i = 0; i <= 10; i++) grad.addColorStop(i / 10, colorScaleRgb(scaleName, i / 10));
      ctx.fillStyle = grad;
      roundRectPath(ctx, x, cyMid - barH / 2, barW, barH, Math.round(2 * m.d));
      ctx.fill();
      ctx.strokeStyle = border;
      ctx.lineWidth = Math.max(1, Math.round(m.d));
      roundRectPath(ctx, x, cyMid - barH / 2, barW, barH, Math.round(2 * m.d));
      ctx.stroke();
      x += barW + m.swGap;
      ctx.fillStyle = textColor;
      ctx.fillText(String(cl.max), x, cyMid);
    }
    return m.lineH + m.vpad * 2;
  };
}

/** Fit readout band: one row per fit - dashed swatch + equation (truncated). */
function fitBand(): BandFn | null {
  const rows = fitReadout.value;
  if (!rows.length) return null;
  return (ctx, w, y, m, draw) => {
    const textColor = getCssVar('--text') || '#e8ebf4';
    const dimColor = getCssVar('--text-dim') || '#8a9099';
    const border = getCssVar('--border') || '#2a2d36';
    ctx.font = m.labelFont;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const label = 'Fits';
    const labelW = ctx.measureText(label).width + m.swGap * 2;
    const rowX = m.pad + labelW;
    const swX = rowX;
    const textX = swX + m.swSize + m.swGap;
    const maxTextW = w - textX - m.pad;
    if (draw) {
      ctx.fillStyle = dimColor;
      ctx.fillText(label, m.pad, y + m.vpad + m.lineH / 2);
    }
    ctx.font = m.font;
    rows.forEach((r, i) => {
      const rowCy = y + m.vpad + m.lineH / 2 + i * (m.lineH + m.rowGap);
      let text = r.series;
      if (r.error) text += `: fit failed`;
      else {
        if (r.eq) text += `: ${r.eq}`;
        if (r.r2 != null) text += `  R²=${r.r2.toFixed(3)}`;
      }
      // Truncate with an ellipsis if the row would overflow.
      while (text.length > 1 && ctx.measureText(text).width > maxTextW) {
        text = text.slice(0, -2) + '…';
      }
      if (draw) {
        drawSwatch(ctx, swX, rowCy, m.swSize, r.color, true, border);
        ctx.fillStyle = r.error ? getCssVar('--danger') || '#ff6b6b' : textColor;
        ctx.fillText(text, textX, rowCy);
      }
    });
    const h = rows.length * m.lineH + Math.max(0, rows.length - 1) * m.rowGap;
    return h + m.vpad * 2;
  };
}

/* ── Compose ────────────────────────────────────────────── */

/**
 * Build the full export canvas: header strips, the live chart canvas, then
 * footer strips, painted over a themed background. Returns a fresh canvas; the
 * caller turns it into PNG/JPG/PDF.
 */
export function composeExportCanvas(srcCanvas: HTMLCanvasElement): HTMLCanvasElement {
  const m = metrics();
  const w = srcCanvas.width;
  const srcH = srcCanvas.height;

  const co = changeoverChips();
  const filterSummary = (plotFilterSummary.value || '').trim();

  const headerBands: BandFn[] = [];
  if (filterSummary) headerBands.push(filterBand(filterSummary));
  if (co) {
    const b = chipBand('Changeovers', co.chips, co.emptyText);
    if (b) headerBands.push(b);
  }

  const footerBands: BandFn[] = [];
  const sChips = seriesChips();
  const seriesB = chipBand('Series', sChips, null);
  if (seriesB) footerBands.push(seriesB);
  const colorB = colorBarBand();
  if (colorB) footerBands.push(colorB);
  const fitB = fitBand();
  if (fitB) footerBands.push(fitB);

  // Measure pass - sum band heights (a 1×1 scratch ctx is enough for measureText).
  const scratch = document.createElement('canvas').getContext('2d');
  if (!scratch) throw new Error('Could not create measuring context');
  const measure = (bands: BandFn[]): number =>
    bands.reduce((acc, b) => acc + b(scratch, w, 0, m, false), 0);
  const headerH = measure(headerBands);
  const footerH = measure(footerBands);

  const out = document.createElement('canvas');
  out.width = w;
  out.height = headerH + srcH + footerH;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('Could not create export context');

  ctx.fillStyle = getTheme() === 'light' ? BG_LIGHT : BG_DARK;
  ctx.fillRect(0, 0, out.width, out.height);

  const border = getCssVar('--border') || '#2a2d36';
  const drawBands = (bands: BandFn[], startY: number): number => {
    let yy = startY;
    for (const b of bands) yy += b(ctx, w, yy, m, true);
    return yy;
  };

  // Header strips, then a hairline, then the chart, then a hairline + footer.
  drawBands(headerBands, 0);
  if (headerH > 0) {
    ctx.strokeStyle = border;
    ctx.lineWidth = Math.max(1, Math.round(m.d));
    ctx.beginPath();
    ctx.moveTo(0, headerH + 0.5);
    ctx.lineTo(w, headerH + 0.5);
    ctx.stroke();
  }
  ctx.drawImage(srcCanvas, 0, headerH);
  if (footerH > 0) {
    ctx.strokeStyle = border;
    ctx.lineWidth = Math.max(1, Math.round(m.d));
    ctx.beginPath();
    ctx.moveTo(0, headerH + srcH + 0.5);
    ctx.lineTo(w, headerH + srcH + 0.5);
    ctx.stroke();
    drawBands(footerBands, headerH + srcH);
  }

  return out;
}
